import { useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, useLocation } from "react-router";
import { useGetList } from "ra-core";
import { Plus, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notification } from "@/components/admin/notification";
import { UserMenu } from "@/components/admin/user-menu";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { DataImportProvider } from "../dataImport/DataImportProvider";
import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { TasksListContent } from "../tasks/TasksListContent";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { NarsStudioLink } from "./NarsStudioLink";
import { NarsRealtime } from "@/nars/NarsRealtime";

const Navigation = () => (
  <nav aria-label="Navigazione principale" className="flex gap-1">
    {[
      ["/", "Oggi"],
      ["/contacts", "Clienti"],
      ["/tasks", "Attività"],
    ].map(([to, label]) => (
      <NavLink
        key={to}
        to={to}
        end={to === "/"}
        className={({ isActive }) =>
          `rounded-md px-4 py-2 text-sm font-medium ${isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`
        }
      >
        {label}
      </NavLink>
    ))}
  </nav>
);

export const NarsLayout = ({
  children,
  demo = false,
}: {
  children: ReactNode;
  demo?: boolean;
}) => {
  useConfigurationLoader();
  const { title, lightModeLogo } = useConfigurationContext();
  const { pathname } = useLocation();
  return (
    <DataImportProvider>
      {!demo && <NarsRealtime />}
      <div className="min-h-dvh bg-background text-foreground">
        <header className="border-b border-border">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-4 md:px-8">
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2 font-semibold tracking-tight"
            >
              <img src={lightModeLogo} alt="" className="size-7" />
              {title}
            </Link>
            <div className="hidden md:block">
              <Navigation />
            </div>
            <div className="flex items-center gap-1">
              <NarsStudioLink compact />
              <div className="hidden sm:block">
                <ThemeModeToggle />
              </div>
              <Button asChild variant="ghost" size="icon">
                <Link to="/settings" aria-label="Impostazioni">
                  <Settings className="size-4" />
                </Link>
              </Button>
              <UserMenu />
            </div>
          </div>
        </header>
        {demo && (
          <div className="border-b border-border bg-muted/40 px-4 py-2 text-center text-xs text-muted-foreground">
            Prova locale · I dati si azzerano ricaricando. La condivisione con
            Pier non è ancora attiva.
          </div>
        )}
        <main
          id="main-content"
          className="mx-auto max-w-5xl px-4 py-8 pb-28 md:px-8 md:pb-12"
        >
          {pathname.startsWith("/deals") || pathname === "/companies" ? (
            <Navigate to="/contacts" replace />
          ) : (
            children
          )}
        </main>
        <div className="fixed inset-x-0 bottom-0 z-20 flex justify-center border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden">
          <Navigation />
        </div>
        <Notification mobileOffset={{ bottom: "80px" }} />
      </div>
    </DataImportProvider>
  );
};

export const NarsDemoLayout = ({ children }: { children: ReactNode }) => (
  <NarsLayout demo>{children}</NarsLayout>
);

export const NarsDashboard = () => (
  <div className="space-y-10">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">
          NARS / Studio
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Oggi</h1>
        <p className="mt-2 text-muted-foreground">
          Ricontatti e attività assegnate a te.
        </p>
      </div>
      <Button asChild>
        <Link to="/contacts/create">
          <Plus className="size-4" /> Aggiungi cliente
        </Link>
      </Button>
    </div>
    <section className="border-t border-border pt-6">
      <h2 className="mb-5 text-lg font-medium">Prossimi passi</h2>
      <TasksListContent />
    </section>
    <Link
      to="/contacts"
      className="inline-block text-sm underline underline-offset-4"
    >
      Vai ai clienti →
    </Link>
  </div>
);

export const NarsTasks = () => {
  const [open, setOpen] = useState(false);
  const { total, isPending, error, refetch } = useGetList("contacts", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
    filter: {},
  });
  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Attività</h1>
          <p className="mt-2 text-muted-foreground">
            I prossimi passi con i tuoi clienti, in ordine di scadenza.
          </p>
        </div>
        {!error && !!total && (
          <Button onClick={() => setOpen(true)}>
            <Plus className="size-4" /> Aggiungi attività
          </Button>
        )}
      </div>
      <TasksListContent />
      {isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Caricamento clienti…
        </p>
      )}
      {error && (
        <div role="alert" className="space-y-3">
          <p>Non riesco a caricare i clienti per aggiungere un’attività.</p>
          <Button variant="outline" onClick={() => refetch()}>
            Riprova
          </Button>
        </div>
      )}
      {!isPending && !error && total === 0 && (
        <Button asChild variant="outline">
          <Link to="/contacts/create">Aggiungi il primo cliente</Link>
        </Button>
      )}
      <TaskCreateSheet open={open} onOpenChange={setOpen} />
    </div>
  );
};
